import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { FollowupPanel } from '@/features/activity/components/TimelinePanel';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { DocumentActions } from '@/shared/components/documents/DocumentActions';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Drawer } from '@/shared/components/ui/Drawer';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useComplaintReportSummary, useServiceComplaint, useServiceComplaints, useServiceDeskLookups, useServiceDeskMutations } from '../api';
import { MasterListModal } from './MasterListModal';
import type { ComplaintFilters, ServiceComplaint, SourceChannel, TicketPriority } from '../types';
import { formatDateTime, humanize, personName, priorityTone } from '../viewModel';

const textareaClass =
  'w-full rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40';

const COMPLAINT_STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'Analysis', value: 'analysis' },
  { label: 'Action', value: 'action' },
  { label: 'Verify', value: 'verify' },
  { label: 'Closed', value: 'closed' },
];

const COMPLAINT_CATEGORY_OPTIONS = [
  { label: 'All categories', value: '' },
  { label: 'Chargeable', value: 'chargeable' },
  { label: 'Non-chargeable', value: 'non_chargeable' },
];

const PRIORITY_OPTIONS: { label: string; value: TicketPriority }[] = [
  { label: 'Normal', value: 'normal' },
  { label: 'High', value: 'high' },
  { label: 'Critical', value: 'critical' },
];

const CHANNEL_OPTIONS: { label: string; value: SourceChannel }[] = [
  { label: 'Web', value: 'web' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Email', value: 'email' },
  { label: 'Portal', value: 'portal' },
  { label: 'System', value: 'system' },
];

function complaintStatusTone(status: ServiceComplaint['cmsStatus']) {
  if (status === 'closed') return 'success';
  if (status === 'verify') return 'warning';
  if (status === 'action') return 'info';
  return 'neutral';
}

function currency(amount: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);
}

function apiError(err: unknown, fallback: string) {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

export function ComplaintsTab() {
  const [filters, setFilters] = useState<ComplaintFilters>({ cmsStatus: '', cmsCategory: '', complaintTypeId: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: summary } = useComplaintReportSummary();
  const { data: complaintList, isLoading, isError } = useServiceComplaints(filters);
  const complaints = complaintList?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-title-lg font-semibold text-on-surface">CMS Complaints</h2>
          <p className="text-body-sm text-on-surface-variant">8D complaint intake, action, verification, closure, and quotation escalation.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTypesOpen(true)}>Complaint types</Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            New Complaint
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard title="Open Stages" value={(summary?.statusCounts.open ?? 0) + (summary?.statusCounts.analysis ?? 0) + (summary?.statusCounts.action ?? 0) + (summary?.statusCounts.verify ?? 0)} />
        <MetricCard title="Closed Complaints" value={summary?.tat.closedCount ?? 0} />
        <MetricCard title="Avg TAT (hrs)" value={summary?.tat.averageHours ?? '—'} />
        <MetricCard title="Chargeable Revenue" value={currency(summary?.revenueByCategory.chargeable ?? 0)} />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Select label="Status" name="complaint-status" options={COMPLAINT_STATUS_OPTIONS} value={filters.cmsStatus ?? ''} onChange={(e) => setFilters((current) => ({ ...current, cmsStatus: e.target.value as ComplaintFilters['cmsStatus'] }))} />
        </div>
        <div className="w-44">
          <Select label="Category" name="complaint-category" options={COMPLAINT_CATEGORY_OPTIONS} value={filters.cmsCategory ?? ''} onChange={(e) => setFilters((current) => ({ ...current, cmsCategory: e.target.value as ComplaintFilters['cmsCategory'] }))} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Failed to load complaints" description="Refresh and try again." />
      ) : complaints.length === 0 ? (
        <EmptyState title="No complaints in this view" description="Raise a complaint or loosen the filters." action={<Button onClick={() => setCreateOpen(true)}>New Complaint</Button>} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <table className="w-full min-w-[900px] text-left text-body-sm">
            <thead className="bg-surface-variant text-on-surface-variant">
              <tr>
                <th className="px-3 py-2 font-medium">CMS No</th>
                <th className="px-3 py-2 font-medium">Complaint</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Expected</th>
              </tr>
            </thead>
            <tbody>
              {complaints.map((complaint) => (
                <tr key={complaint.id} className="cursor-pointer border-t border-outline bg-surface hover:bg-surface-variant/60" onClick={() => setSelectedId(complaint.id)}>
                  <td className="px-3 py-2 font-mono text-data-md text-on-surface">{complaint.cmsNo}</td>
                  <td className="px-3 py-2 text-on-surface">{complaint.title}</td>
                  <td className="px-3 py-2 text-on-surface-variant">{typeof complaint.complaintTypeId === 'object' && complaint.complaintTypeId ? complaint.complaintTypeId.name : 'Unspecified'}</td>
                  <td className="px-3 py-2"><Badge tone={complaint.cmsCategory === 'chargeable' ? 'warning' : 'neutral'}>{humanize(complaint.cmsCategory)}</Badge></td>
                  <td className="px-3 py-2"><Badge tone={complaintStatusTone(complaint.cmsStatus)}>{humanize(complaint.cmsStatus)}</Badge></td>
                  <td className="px-3 py-2 text-on-surface">{currency(complaint.amount)}</td>
                  <td className="px-3 py-2 text-on-surface-variant">{formatDateTime(complaint.expectedCompletion)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateComplaintModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {typesOpen && <MasterListModal masterType="complaint_type" title="Complaint types" onClose={() => setTypesOpen(false)} />}
      <ComplaintDrawer complaintId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function MetricCard({ title, value }: { title: string; value: string | number }) {
  return <Card><CardHeader title={title} /><CardBody className="text-2xl font-semibold text-on-surface">{value}</CardBody></Card>;
}

function CreateComplaintModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { createComplaint } = useServiceDeskMutations();
  const { data: complaintTypes = [] } = useMasterItems('complaint_type');
  const { data: lookups } = useServiceDeskLookups(open);
  const { data: users = [] } = useUserOptions(null);
  
  const [form, setForm] = useState({ title: '', description: '', sourceChannel: 'web' as SourceChannel, priority: 'normal' as TicketPriority, complaintTypeId: '', cmsCategory: 'non_chargeable' as ServiceComplaint['cmsCategory'], serialNo: '', purchaseDate: '', invoiceNo: '', productId: '', productQuantity: '1', dealerId: '', employeeId: '', expectedCompletion: '', amount: '0', instructions: '' });
  const select = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));

  const submit = () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required.');
      return;
    }
    createComplaint.mutate({
      title: form.title.trim(),
      description: form.description.trim(),
      sourceChannel: form.sourceChannel,
      priority: form.priority,
      complaintTypeId: form.complaintTypeId || undefined,
      cmsCategory: form.cmsCategory,
      serialNo: form.serialNo || undefined,
      purchaseDate: form.purchaseDate || undefined,
      invoiceNo: form.invoiceNo || undefined,
      productId: form.productId || undefined,
      productQuantity: Number(form.productQuantity) || 0,
      dealerId: form.dealerId || undefined,
      employeeId: form.employeeId || undefined,
      expectedCompletion: form.expectedCompletion || undefined,
      amount: Number(form.amount) || 0,
      instructions: form.instructions || undefined,
    }, {
      onSuccess: (complaint) => { toast.success(`Complaint ${complaint.cmsNo} created.`); onClose(); },
      onError: (err) => toast.error(apiError(err, 'Failed to create complaint.')),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="New Complaint" footer={<div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={createComplaint.isPending}>{createComplaint.isPending ? 'Creating…' : 'Create Complaint'}</Button></div>}>
      <div className="grid gap-3 md:grid-cols-2">
        <Input label="Title" name="complaint-title" value={form.title} onChange={(e) => select('title', e.target.value)} />
        <Select label="Complaint type" name="complaint-type" value={form.complaintTypeId} onChange={(e) => select('complaintTypeId', e.target.value)} options={[{ label: 'Select complaint type', value: '' }, ...complaintTypes.map((item) => ({ label: item.name, value: item._id }))]} />
        <div className="md:col-span-2">
          <label htmlFor="complaint-description" className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea id="complaint-description" rows={4} className={textareaClass} value={form.description} onChange={(e) => select('description', e.target.value)} />
        </div>
        <Select label="Category" name="complaint-category-new" value={form.cmsCategory} onChange={(e) => select('cmsCategory', e.target.value as ServiceComplaint['cmsCategory'])} options={[{ label: 'Chargeable', value: 'chargeable' }, { label: 'Non-chargeable', value: 'non_chargeable' }]} />
        <Select label="Priority" name="complaint-priority" value={form.priority} onChange={(e) => select('priority', e.target.value as TicketPriority)} options={PRIORITY_OPTIONS} />
        <Select label="Channel" name="complaint-channel" value={form.sourceChannel} onChange={(e) => select('sourceChannel', e.target.value as SourceChannel)} options={CHANNEL_OPTIONS} />
        <Input label="Serial no." name="complaint-serial" value={form.serialNo} onChange={(e) => select('serialNo', e.target.value)} />
        <Input label="Purchase date" name="complaint-purchase-date" type="date" value={form.purchaseDate} onChange={(e) => select('purchaseDate', e.target.value)} />
        <Input label="Invoice no." name="complaint-invoice" value={form.invoiceNo} onChange={(e) => select('invoiceNo', e.target.value)} />
        <Select label="Product" name="complaint-product" value={form.productId} onChange={(e) => select('productId', e.target.value)} options={[{ label: 'No product', value: '' }, ...(lookups?.products ?? []).map((product) => ({ label: product.name, value: product.id }))]} />
        <Input label="Qty" name="complaint-qty" type="number" min={0} value={form.productQuantity} onChange={(e) => select('productQuantity', e.target.value)} />
        <Select label="Dealer" name="complaint-dealer" value={form.dealerId} onChange={(e) => select('dealerId', e.target.value)} options={[{ label: 'Select dealer', value: '' }, ...(lookups?.clients ?? []).map((customer) => ({ label: customer.company || customer.name, value: customer.id }))]} />
        <Select label="Employee" name="complaint-employee" value={form.employeeId} onChange={(e) => select('employeeId', e.target.value)} options={[{ label: 'Select employee', value: '' }, ...users.map((user) => ({ label: user.display_name, value: user.id }))]} />
        <Input label="Expected completion" name="complaint-expected" type="datetime-local" value={form.expectedCompletion} onChange={(e) => select('expectedCompletion', e.target.value)} />
        <Input label="Amount" name="complaint-amount" type="number" min={0} value={form.amount} onChange={(e) => select('amount', e.target.value)} />
        <div className="md:col-span-2">
          <label htmlFor="complaint-instructions" className="mb-1.5 block text-body-sm font-medium text-on-surface">Instructions</label>
          <textarea id="complaint-instructions" rows={3} className={textareaClass} value={form.instructions} onChange={(e) => select('instructions', e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

function ComplaintDrawer({ complaintId, onClose }: { complaintId: string | null; onClose: () => void }) {
  const { data: complaint, isLoading } = useServiceComplaint(complaintId);
  const { updateComplaint, transitionComplaint } = useServiceDeskMutations();
  const [draft, setDraft] = useState({ containmentAction: '', rootCause: '', correctiveAction: '', preventiveAction: '', feedback: '', completionDate: '', investigation: '', action: '', supervision: '' });

  useEffect(() => {
    if (!complaint) return;
    setDraft({
      containmentAction: complaint.containmentAction || '',
      rootCause: complaint.rootCause || '',
      correctiveAction: complaint.correctiveAction || '',
      preventiveAction: complaint.preventiveAction || '',
      feedback: complaint.feedback || '',
      completionDate: complaint.completionDate ? String(complaint.completionDate).slice(0, 16) : '',
      investigation: complaint.eightD?.investigation?.observation || '',
      action: complaint.eightD?.action?.action || '',
      supervision: complaint.eightD?.supervision?.comments || '',
    });
  }, [complaint]);

  const save = () => {
    if (!complaint) return;
    updateComplaint.mutate({
      id: complaint.id,
      containmentAction: draft.containmentAction,
      rootCause: draft.rootCause,
      correctiveAction: draft.correctiveAction,
      preventiveAction: draft.preventiveAction,
      feedback: draft.feedback,
      completionDate: draft.completionDate || undefined,
      eightD: {
        investigation: { observation: draft.investigation },
        action: { action: draft.action },
        supervision: { comments: draft.supervision },
      },
    }, { onSuccess: () => toast.success('Complaint details saved.'), onError: (err) => toast.error(apiError(err, 'Could not save complaint changes.')) });
  };

  return (
    <Drawer open={!!complaintId} onClose={onClose} width="w-[680px]" title={complaint ? `${complaint.cmsNo} — ${complaint.title}` : 'Complaint'}>
      {isLoading || !complaint ? <div className="flex h-40 items-center justify-center"><Spinner /></div> : (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone={complaintStatusTone(complaint.cmsStatus)}>{humanize(complaint.cmsStatus)}</Badge>
            <Badge tone={complaint.cmsCategory === 'chargeable' ? 'warning' : 'neutral'}>{humanize(complaint.cmsCategory)}</Badge>
            <Badge tone={priorityTone(complaint.priority)}>{humanize(complaint.priority)}</Badge>
          </div>

          <Card>
            <CardHeader title="Complaint Details" subtitle={typeof complaint.complaintTypeId === 'object' && complaint.complaintTypeId ? complaint.complaintTypeId.name : 'Unspecified type'} />
            <CardBody className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
              <span className="text-on-surface-variant">Dealer</span><span className="text-on-surface">{typeof complaint.dealerId === 'object' && complaint.dealerId ? complaint.dealerId.name || '—' : '—'}</span>
              <span className="text-on-surface-variant">Employee</span><span className="text-on-surface">{personName(complaint.employeeId, '—')}</span>
              <span className="text-on-surface-variant">Product</span><span className="text-on-surface">{typeof complaint.productId === 'object' && complaint.productId ? complaint.productId.name || '—' : '—'}</span>
              <span className="text-on-surface-variant">Amount</span><span className="text-on-surface">{currency(complaint.amount)}</span>
              <span className="text-on-surface-variant">Expected completion</span><span className="text-on-surface">{formatDateTime(complaint.expectedCompletion)}</span>
              <span className="text-on-surface-variant">Created</span><span className="text-on-surface">{formatDateTime(complaint.createdAt)}</span>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Workflow" action={<DocumentActions docType="service_ticket" docId={complaint.id} />} />
            <CardBody className="flex flex-wrap gap-2">
              {complaint.availableCmsTransitions?.map((transition) => (
                <Button key={transition.to} size="sm" variant="secondary" disabled={transitionComplaint.isPending} onClick={() => transitionComplaint.mutate({ id: complaint.id, toStatus: transition.to }, { onSuccess: () => toast.success(`${transition.label} complete.`), onError: (err) => toast.error(apiError(err, 'Transition failed.')) })}>{transition.label}</Button>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="8D Investigation" subtitle="Stamped updates are written with the current user and time." />
            <CardBody className="grid gap-3">
              <FieldArea id="complaint-investigation" label="Investigation observation" value={draft.investigation} onChange={(value) => setDraft((current) => ({ ...current, investigation: value }))} />
              <FieldArea id="complaint-containment" label="Containment action" value={draft.containmentAction} onChange={(value) => setDraft((current) => ({ ...current, containmentAction: value }))} />
              <FieldArea id="complaint-root-cause" label="Root cause" value={draft.rootCause} onChange={(value) => setDraft((current) => ({ ...current, rootCause: value }))} />
              <FieldArea id="complaint-action" label="Action taken" value={draft.action} onChange={(value) => setDraft((current) => ({ ...current, action: value }))} />
              <FieldArea id="complaint-corrective" label="Corrective action" value={draft.correctiveAction} onChange={(value) => setDraft((current) => ({ ...current, correctiveAction: value }))} />
              <FieldArea id="complaint-preventive" label="Preventive action" value={draft.preventiveAction} onChange={(value) => setDraft((current) => ({ ...current, preventiveAction: value }))} />
              <FieldArea id="complaint-supervision" label="Supervision comments" value={draft.supervision} onChange={(value) => setDraft((current) => ({ ...current, supervision: value }))} />
              <div className="grid gap-3 md:grid-cols-2">
                <Input label="Completion date" name="complaint-completion-date" type="datetime-local" value={draft.completionDate} onChange={(e) => setDraft((current) => ({ ...current, completionDate: e.target.value }))} />
                <FieldArea id="complaint-feedback" label="Feedback" value={draft.feedback} onChange={(value) => setDraft((current) => ({ ...current, feedback: value }))} rows={3} />
              </div>
              <div className="flex justify-end"><Button onClick={save} disabled={updateComplaint.isPending}>{updateComplaint.isPending ? 'Saving…' : 'Save Complaint Details'}</Button></div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Followups" subtitle="Complaint followups use the shared activity log introduced in Phase 0.1." />
            <CardBody><FollowupPanel parentType="service_ticket" parentId={complaint.id} /></CardBody>
          </Card>

          <Card>
            <CardHeader title="Closure Snapshot" />
            <CardBody className="grid grid-cols-2 gap-x-4 gap-y-2 text-body-sm">
              <span className="text-on-surface-variant">Completion</span><span className="text-on-surface">{formatDateTime(complaint.completionDate)}</span>
              <span className="text-on-surface-variant">Feedback</span><span className="text-on-surface whitespace-pre-wrap">{complaint.feedback || '—'}</span>
            </CardBody>
          </Card>
        </div>
      )}
    </Drawer>
  );
}

function FieldArea({ id, label, value, onChange, rows = 3 }: { id: string; label: string; value: string; onChange: (value: string) => void; rows?: number }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-body-sm font-medium text-on-surface">{label}</label>
      <textarea id={id} rows={rows} className={textareaClass} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
